import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isSdk429Error, isSdkFetchCached, isSdkRateLimited } from '../util/sdkRateLimit';
import { isGlobalSdkRateLimited, scheduleSdkRequest } from '../util/sdkRequestQueue';

/**
 * Thunk to fetch all published listings for a provider
 */
const fetchProviderListings = (providerId, config) => (dispatch, getState, sdk) => {
  if (!providerId || !sdk) {
    return Promise.resolve({ data: { data: [] } });
  }

  return scheduleSdkRequest(() =>
    sdk.listings.query({
      author_id: providerId,
      state: 'published',
      perPage: 100,
      include: ['author', 'author.profileImage', 'currentStock'],
      'fields.listing': [
        'title',
        'state',
        'price',
        'availabilityPlan',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        'publicData.startTimeInterval',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'publicData.categoryLevel3',
      ],
    })
  );
};

const providerListingsCache = new Map();

/**
 * Custom hook to fetch all published listings for a provider
 * @param {string} providerId - The provider user ID
 * @param {Object} config - The app configuration
 * @param {number} [deferFetchMs=0] - Delay before fetch (stagger cards on search page)
 * @returns {Object} - Object containing listings, loading state, and error
 */
export const useProviderListings = (providerId, config, deferFetchMs = 0) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (!providerId || !config) {
      return undefined;
    }

    const cached = providerListingsCache.get(providerId);
    if (
      cached &&
      isSdkFetchCached(cached.fetchedAt) &&
      !isSdkRateLimited(cached.rateLimitedUntil) &&
      !isGlobalSdkRateLimited()
    ) {
      setListings(cached.listings);
      setLoading(false);
      setError(cached.error || null);
      return undefined;
    }

    if (cached?.inFlightPromise) {
      setLoading(true);
      cached.inFlightPromise
        .then(published => {
          setListings(published);
          setLoading(false);
          setError(null);
        })
        .catch(e => {
          setError(e);
          setLoading(false);
        });
      return undefined;
    }

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) {
        return;
      }

      setLoading(true);
      setError(null);

      const inFlightPromise = dispatch(fetchProviderListings(providerId, config))
        .then(response => {
          const results = response?.data?.data || [];
          const published = results.filter(
            listing => !listing.attributes?.deleted && listing.attributes?.state === 'published'
          );
          providerListingsCache.set(providerId, {
            listings: published,
            fetchedAt: Date.now(),
            loading: false,
            error: null,
            rateLimitedUntil: null,
            inFlightPromise: null,
          });
          return published;
        })
        .catch(e => {
          const rateLimited = isSdk429Error(e);
          providerListingsCache.set(providerId, {
            listings: cached?.listings || [],
            fetchedAt: Date.now(),
            loading: false,
            error: e,
            rateLimitedUntil: rateLimited ? Date.now() + 60 * 1000 : null,
            inFlightPromise: null,
          });
          throw e;
        });

      providerListingsCache.set(providerId, {
        ...cached,
        inFlightPromise,
      });

      inFlightPromise
        .then(published => {
          if (!cancelled) {
            setListings(published);
            setLoading(false);
          }
        })
        .catch(e => {
          if (!cancelled) {
            setError(e);
            setLoading(false);
          }
        });
    }, deferFetchMs);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [dispatch, providerId, config, deferFetchMs]);

  return { listings, loading, error };
};
